import {
  Currencies,
  SchoolEducationSystem,
  SchoolPropertyType,
  SchoolType,
} from '@schooly/constants';

import type { AttendanceFrequencyResponse } from './endpoints/attendance';
import { TermlyPaymentFrequency } from '../frequencies';
import { CustomFieldDataType } from './endpoints/customFields';
import { GetCategoriesResponse } from './endpoints/school';
import { NumberOrNull, StringOrNull } from './misc';
import { WithAddress } from './partials';
import { RegistrationStatus } from './registrations';

export enum SchoolVerificationStatus {
  NotRequested = 'not-requested',
  Pending = 'pending',
  Verified = 'verified',
}

export interface School extends WithAddress {
  id: string;
  code: string;
  name: string;
  image?: StringOrNull;
  is_public_listing?: boolean;
  verification?: boolean;
  verification_status?: SchoolVerificationStatus;
  listed?: boolean;
  description?: StringOrNull;
  telephone?: StringOrNull;
  website?: StringOrNull;
  contact_email?: StringOrNull;
  type?: SchoolType;
  education_system?: SchoolEducationSystem | null;
  currency?: Currencies;
  is_trial: boolean;
  trial_expiry_date: string | null;
}

export type DirectorySchoolItem<T> = { label: T; id: string };
export interface DirectorySchool extends School {
  curriculums: Array<DirectorySchoolItem<string>>;
  languages: {
    primary: Array<DirectorySchoolItem<string>> | null;
    taught: Array<DirectorySchoolItem<string>> | null;
  };
  students_age: Array<DirectorySchoolItem<number>> | null;
  fees: StringOrNull;
  students_count: NumberOrNull;
  foundation_year: StringOrNull;
  students_teacher_ratio: StringOrNull;
  accreditation: StringOrNull;
  school_type: StringOrNull;
}

export interface SchoolYearPeriod {
  id: string;
  name: string;
  date_from: string;
  date_to: string;
}

export type SchoolYearPeriodGroup = {
  id: string;
  name: string;
  should_publish_events: boolean;
  periods: Array<SchoolYearPeriod>;
  frequency: TermlyPaymentFrequency | null;
  main: boolean;
};

export type SchoolYearInUse = {
  periods: number;
  groups: number;
  registrations_with_current_status: {
    applies_from: null | string;
    count: number;
  };
  has_started: boolean;
  related_registrations: number;
};
export interface SchoolYear {
  id: string;
  name: string;
  start: string;
  end: string;
  can_be_deleted?: boolean;
  period_groups?: Array<SchoolYearPeriodGroup>;
  in_use_info: SchoolYearInUse;
}

export interface DefaultSchoolYear extends SchoolYear {
  isActual?: boolean;
}

export interface SchoolYearUpdate {
  // `school_year_id` is used to update existing school year by id
  school_year_id: string;
  name: string;
  start: string;
  end: string;
}

export type SchoolPropertyCategory = GetCategoriesResponse['categories'][number];

export const PENDING_PARENT_RESPONSE = 'Pending parent response' as const;
export const EXPIRED_RE_ENROLLMENT = 'Expired re-enrollment' as const;

type ReEnrollmentSource = {
  type: 're_enrollment';
  read_only: boolean;
};

export interface SchoolProperty {
  id: string;
  type: SchoolPropertyType;
  name: string;
  order: number;
  archived?: boolean;
  category?: SchoolPropertyCategory | null;
  student_default?: boolean;
  staff_default?: boolean;
  group_default?: boolean;
  conduct_default?: boolean;
  re_enrollment_default?: boolean;
  source?: ReEnrollmentSource | null;
}
export interface Registration {
  id: string;
  initial: boolean;
  school_properties: SchoolProperty[];
  school_year: SchoolYear;
  statuses: RegistrationStatus[];
}

export interface SchoolLevel {
  id: string;
  name: string;
  order: number;
}
export interface AgeGroup {
  id: string;
  name: string;
  order: number;
  archived?: boolean;
  level_id: string | null;
  student_default?: boolean;
  staff_default?: boolean;
  group_default?: boolean;
  conduct_default?: boolean;
  attendance_frequency?: AttendanceFrequencyResponse | null;
}

export enum DemoSchoolType {
  UK = 'UK',
  BR = 'BR',
}

export enum TextOperator {
  All = 'all',
  None = 'none',
  Contains = 'contains',
}
export enum BooleanOperator {
  All = 'all',
  None = 'none',
  IsEqual = 'is_equal',
}
export enum DateOperator {
  All = 'all',
  None = 'none',
  IsEqual = 'is_equal',
  InRange = 'in_range',
}
export enum SelectListOperator {
  All = 'all',
  None = 'none',
  IsOneOf = 'is_one_of',
}

export type MedicalNeedFilter =
  | {
      operator: TextOperator.All;
      value: undefined;
      severity: boolean | undefined;
      reviewed_by_nurse: boolean | undefined;
    }
  | {
      operator: TextOperator.Contains;
      value: string;
      severity: boolean | undefined;
      reviewed_by_nurse: boolean | undefined;
    }
  | {
      operator: TextOperator.None;
      value: undefined;
      severity: undefined;
      reviewed_by_nurse: undefined;
    };

export type CustomFieldTextNumberFilter = {
  id: string;
  type: CustomFieldDataType.STRING | CustomFieldDataType.NUMBER;
} & (
  | {
      operator: TextOperator;
      value: string;
    }
  | {
      operator: TextOperator.All | TextOperator.None;
      value: undefined;
    }
);

export type CustomFieldDateFilter = {
  id: string;
  type: CustomFieldDataType.DATE;
} & (
  | {
      operator: DateOperator.InRange;
      value: [string, string];
    }
  | {
      operator: DateOperator.IsEqual;
      value: [string];
    }
  | {
      operator: DateOperator.All | DateOperator.None;
      value: undefined;
    }
);

export type CustomFieldSelectListFilter = {
  id: string;
  type:
    | CustomFieldDataType.SELECT
    | CustomFieldDataType.ARR_STRING
    | CustomFieldDataType.ARR_NUMBER;
} & (
  | {
      operator: SelectListOperator;
      value: string[];
    }
  | {
      operator: SelectListOperator.All | SelectListOperator.None;
      value: undefined;
    }
);

export type CustomFieldBooleanFilter = {
  id: string;
  type: CustomFieldDataType.BOOLEAN;
} & (
  | {
      operator: BooleanOperator.IsEqual;
      value: boolean;
    }
  | {
      operator: BooleanOperator.All | BooleanOperator.None;
      value: undefined;
    }
);

export type CustomFieldFilter =
  | CustomFieldTextNumberFilter
  | CustomFieldDateFilter
  | CustomFieldSelectListFilter
  | CustomFieldBooleanFilter;
